import { Box, styled } from "@mui/material";

export const IOS_SCREEN_WIDTH = 393;
export const IOS_SCREEN_HEIGHT = 852;
const BEZEL = 12;
export const IOS_PHONE_WIDTH = IOS_SCREEN_WIDTH + BEZEL * 2;
export const IOS_PHONE_HEIGHT = IOS_SCREEN_HEIGHT + BEZEL * 2;

export const IOS_FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif';

export const IOS = {
  ink: "#000000",
  blue: "#007AFF",
  grey: "#8E8E93",
  bubble: "#E9E9EB",
  fieldBorder: "#C6C6C8",
  hairline: "rgba(60, 60, 67, 0.29)",
  chrome: "#F9F9F9",
  screen: "#FFFFFF",
  bezel: "#0F0F10",
  bezelEdge: "#4A4A4D",
} as const;

export const Device = styled(Box)({
  width: IOS_PHONE_WIDTH,
  height: IOS_PHONE_HEIGHT,
  padding: BEZEL - 2,
  border: `2px solid ${IOS.bezelEdge}`,
  borderRadius: 67,
  backgroundColor: IOS.bezel,
  boxSizing: "border-box",
  fontFamily: IOS_FONT,
  color: IOS.ink,
  WebkitFontSmoothing: "antialiased",
  "& *": { boxSizing: "border-box" },
});

export const Screen = styled(Box)({
  position: "relative",
  width: IOS_SCREEN_WIDTH,
  height: IOS_SCREEN_HEIGHT,
  borderRadius: 55,
  overflow: "hidden",
  clipPath: "inset(0 round 55px)",
  backgroundColor: IOS.screen,
});

export const DynamicIsland = styled(Box)({
  position: "absolute",
  top: 11,
  left: "50%",
  transform: "translateX(-50%)",
  width: 125,
  height: 37,
  borderRadius: 20,
  backgroundColor: "#000",
  zIndex: 5,
});

export const StatusBar = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 54,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 46px 0 52px",
  fontSize: 17,
  fontWeight: 600,
  zIndex: 4,
  "& > span": { display: "flex", gap: 6, alignItems: "center" },
  "& svg": { display: "block" },
});

export const HomeBar = styled(Box)({
  position: "absolute",
  bottom: 8,
  left: "50%",
  transform: "translateX(-50%)",
  width: 134,
  height: 5,
  borderRadius: 3,
  backgroundColor: "currentColor",
});

export const NavBar = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 150,
  backgroundColor: IOS.chrome,
  borderBottom: `0.5px solid ${IOS.hairline}`,
  zIndex: 3,
});

export const NavBack = styled(Box)({
  position: "absolute",
  left: 8,
  top: 80,
  width: 44,
  height: 44,
  display: "grid",
  placeItems: "center",
  color: IOS.blue,
});

export const NavIdentity = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  top: 60,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 3,
});

export const NavAvatar = styled(Box)({
  width: 50,
  height: 50,
  borderRadius: "50%",
  background: "linear-gradient(180deg, #A5A8AE, #8A8D93)",
  color: "#fff",
  display: "grid",
  placeItems: "center",
});

export const NavName = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 2,
  maxWidth: 220,
  fontSize: 11,
  lineHeight: "13px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "& svg": { flex: "0 0 auto", color: IOS.grey },
});

export const NavVideo = styled(Box)({
  position: "absolute",
  right: 14,
  top: 82,
  width: 44,
  height: 44,
  display: "grid",
  placeItems: "center",
  color: IOS.blue,
});

export const Thread = styled(Box)({
  position: "absolute",
  top: 150,
  left: 0,
  right: 0,
  bottom: 90,
  padding: "16px 16px 8px",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
});

export const ThreadStack = styled(Box)({
  marginTop: "auto",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 8,
});

export const Stamp = styled(Box)({
  alignSelf: "center",
  fontSize: 11,
  lineHeight: "13px",
  color: IOS.grey,
  marginBottom: 8,
  "& b": { fontWeight: 600 },
});

export const Bubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hint",
})<{ hint?: boolean }>(({ hint }) => ({
  position: "relative",
  maxWidth: 270,
  minHeight: 38,
  padding: "8px 12px",
  borderRadius: 18,
  backgroundColor: IOS.bubble,
  color: hint ? IOS.grey : IOS.ink,
  fontSize: 17,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  "& .preview-url": { color: IOS.blue, textDecoration: "underline" },
  "&::before": {
    content: '""',
    position: "absolute",
    left: -7,
    bottom: 0,
    width: 20,
    height: 20,
    backgroundColor: IOS.bubble,
    borderBottomRightRadius: "16px 14px",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    left: -10,
    bottom: 0,
    width: 10,
    height: 20,
    backgroundColor: IOS.screen,
    borderBottomRightRadius: 10,
  },
}));

export const ImageBubble = styled(Box)({
  maxWidth: 270,
  borderRadius: 18,
  overflow: "hidden",
  "& img": { display: "block", width: "100%", height: "auto" },
});

export const RcsCta = styled(Box, {
  shouldForwardProp: (prop) => prop !== "divided",
})<{ divided: boolean }>(({ divided }) => ({
  marginTop: divided ? 8 : 0,
  paddingTop: divided ? 8 : 0,
  borderTop: divided ? `1px solid ${IOS.hairline}` : "none",
  textAlign: "center",
  color: IOS.blue,
  fontWeight: 600,
}));

export const PillRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

export const Pill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 14px",
  borderRadius: 999,
  backgroundColor: IOS.screen,
  border: `1px solid ${IOS.fieldBorder}`,
  color: IOS.blue,
  fontSize: 15,
  lineHeight: "20px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  "& svg": { fontSize: 16 },
});

export const Composer = styled(Box)({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: 90,
  padding: "8px 16px 0 8px",
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  backgroundColor: IOS.chrome,
  borderTop: `0.5px solid ${IOS.hairline}`,
});

export const ComposerPlus = styled(Box)({
  flex: "0 0 auto",
  width: 34,
  height: 34,
  borderRadius: "50%",
  backgroundColor: IOS.bubble,
  color: "#5C5C61",
  display: "grid",
  placeItems: "center",
});

export const ComposerField = styled(Box)({
  flex: 1,
  minWidth: 0,
  height: 34,
  padding: "0 10px 0 12px",
  border: `1px solid ${IOS.fieldBorder}`,
  borderRadius: 17,
  backgroundColor: IOS.screen,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: 17,
  color: IOS.grey,
  whiteSpace: "nowrap",
});

export const LockRoot = styled(Box)({
  position: "absolute",
  inset: 0,
  color: "#fff",
  background: [
    "radial-gradient(120% 80% at 20% 15%, #F2A7C7 0%, rgba(242,167,199,0) 60%)",
    "radial-gradient(90% 70% at 85% 30%, #7FB3FF 0%, rgba(127,179,255,0) 60%)",
    "radial-gradient(90% 80% at 50% 95%, #2B1F6B 0%, rgba(43,31,107,0) 70%)",
    "linear-gradient(180deg, #5D3FC4 0%, #2A2A7A 60%, #0E1233 100%)",
  ].join(", "),
});

export const LockDate = styled(Box)({
  position: "absolute",
  top: 86,
  left: 0,
  right: 0,
  textAlign: "center",
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: "-0.01em",
});

export const LockTime = styled(Box)({
  position: "absolute",
  top: 104,
  left: 0,
  right: 0,
  textAlign: "center",
  fontSize: 96,
  lineHeight: 1,
  fontWeight: 500,
  letterSpacing: "-0.03em",
});

export const LockButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "side",
})<{ side: "left" | "right" }>(({ side }) => ({
  position: "absolute",
  bottom: 64,
  [side]: 44,
  width: 50,
  height: 50,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.18)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  display: "grid",
  placeItems: "center",
}));

export const Banner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "withThumb",
})<{ withThumb: boolean }>(({ withThumb }) => ({
  position: "absolute",
  left: 8,
  right: 8,
  bottom: 132,
  padding: 14,
  borderRadius: 24,
  display: "grid",
  gridTemplateColumns: withThumb ? "38px 1fr 38px" : "38px 1fr",
  columnGap: 10,
  alignItems: "start",
  backgroundColor: "rgba(240, 240, 244, 0.8)",
  backdropFilter: "blur(40px) saturate(1.4)",
  WebkitBackdropFilter: "blur(40px) saturate(1.4)",
  boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.35)",
  color: IOS.ink,
}));

export const BannerAvatar = styled(Box)({
  position: "relative",
  width: 38,
  height: 38,
  borderRadius: "50%",
  background: "linear-gradient(180deg, #A5A8AE, #8A8D93)",
  color: "#fff",
  display: "grid",
  placeItems: "center",
});

export const BannerBadge = styled(Box)({
  position: "absolute",
  right: -4,
  bottom: -4,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: "linear-gradient(180deg, #6FE27A, #2FC356)",
  border: "2px solid rgba(240, 240, 244, 0.9)",
  color: "#fff",
  display: "grid",
  placeItems: "center",
});

export const BannerTitleRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 8,
  lineHeight: "20px",
  minWidth: 0,
  "& > b": {
    fontSize: 15,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& > span": {
    flex: "0 0 auto",
    fontSize: 13,
    color: "rgba(60, 60, 67, 0.6)",
  },
});

export const BannerBody = styled(Box)({
  fontSize: 15,
  lineHeight: "20px",
  color: "#1C1C1E",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 4,
  overflow: "hidden",
});

export const BannerThumb = styled(Box)({
  width: 38,
  height: 38,
  borderRadius: 8,
  overflow: "hidden",
  "& img": {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});
